import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type AgentRunStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "needs_human";

type AgentRunRow = {
  id: string;
  asin: string;
  customerEmail: string;
  status: AgentRunStatus;
  caseId: string | null;
  costUsd: string | null;
  startedAt: string;
  completedAt: string | null;
  duration: number | null;
};

const STATUS_VARIANT: Record<
  AgentRunStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  running: "default",
  succeeded: "outline",
  failed: "destructive",
  needs_human: "outline",
};

const STATUS_LABEL: Record<AgentRunStatus, string> = {
  pending: "Pending",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  needs_human: "Needs human",
};

const ALL_STATUSES: AgentRunStatus[] = [
  "pending",
  "running",
  "succeeded",
  "failed",
  "needs_human",
];

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function AdminAgentRunsInner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState<AgentRunRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const statusParam = searchParams.get("status") as AgentRunStatus | null;
  const statusFilter =
    statusParam && ALL_STATUSES.includes(statusParam) ? statusParam : "all";

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRuns(null);
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    api<{ runs: AgentRunRow[] }>(
      `/admin/agent-runs?${params.toString()}`
    ).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error ?? "Failed to load agent runs.");
        return;
      }
      setRuns(res.data?.runs ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent runs</CardTitle>
        <CardDescription>
          Experimental submission runs and operator handoffs. The active customer workflow is manual Brand Registry filing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filter:</span>
          <Button
            variant={statusFilter === "all" ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setSearchParams({})}
          >
            All
          </Button>
          {ALL_STATUSES.map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSearchParams({ status: s })}
            >
              {STATUS_LABEL[s]}
            </Button>
          ))}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {!runs && !error && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {runs && runs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No experimental runs yet. Current Brand Registry submissions are
            prepared from the takedown inbox and filed manually.
          </p>
        )}
        {runs && runs.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>ASIN</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Case ID</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => {
                const awaiting = r.status === "needs_human";
                return (
                  <TableRow
                    key={r.id}
                    onClick={() => navigate(`/admin/agent-runs/${r.id}`)}
                    onAuxClick={(e) => {
                      if (e.button === 1)
                        window.open(`/admin/agent-runs/${r.id}`, "_blank");
                    }}
                    className={
                      awaiting
                        ? "cursor-pointer bg-amber-50 hover:bg-amber-100"
                        : "cursor-pointer hover:bg-muted/50"
                    }
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {r.id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-sm">{r.customerEmail}</TableCell>
                    <TableCell className="font-mono text-sm">{r.asin}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={STATUS_VARIANT[r.status]}>
                          {STATUS_LABEL[r.status]}
                        </Badge>
                        {awaiting && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
                            <AlertTriangle className="h-3 w-3" /> awaiting operator
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.caseId ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {r.costUsd != null ? `$${r.costUsd}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.startedAt.slice(0, 16).replace("T", " ")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {formatDuration(r.duration)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminAgentRuns() {
  return (
    <AdminGate>
      <AdminLayout>
        <AdminAgentRunsInner />
      </AdminLayout>
    </AdminGate>
  );
}
